import { useState } from "react";

import { useRunTrackerSignalScanMutation } from "@/pages/Private/redux/signal/signal.api";

import { RunSignalSearchConfirmModal } from "../components/RunSignalSearchConfirmModal";
import { SignalScanRequests, SignalSearchTarget } from "../providers/SignalScanRequestsContext";

const countOf = (target: SignalSearchTarget) =>
	"all" in target ? target.count : target.ids.length;

/**
 * "Run signal search" for one company or a selection (PREC-3240): ask for confirmation with the
 * cost, start the scans, and put the requested cells on "Looking for signals". A refusal, such as
 * a balance that does not cover the batch, starts nothing and is shown in the dialog, which stays
 * open (PREC-4263; a toast after the dialog closed before).
 *
 * The table owns it, once; its cells start a search through the requests context.
 */
export const useRunSignalSearch = ({ trackerId, markRequested }: SignalScanRequests) => {
	const [runScan, { isLoading }] = useRunTrackerSignalScanMutation();
	const [target, setTarget] = useState<SignalSearchTarget | null>(null);

	const confirm = async () => {
		if (!target || !trackerId) {
			return;
		}

		const body = "all" in target ? { all: true } : { trackerOrganizationIds: target.ids };

		await runScan({ trackerId, ...body }).unwrap();
		markRequested("all" in target ? { all: true } : { ids: target.ids });
		setTarget(null);
	};

	const modal = target ? (
		<RunSignalSearchConfirmModal
			count={countOf(target)}
			onClose={() => setTarget(null)}
			onConfirm={confirm}
		/>
	) : null;

	return { requestSearch: setTarget, modal, isLoading };
};
